import { Container, Row, Col, Tab, Nav } from "react-bootstrap";
import { ProjectCard } from "./ProjectCard";
import projectLabImage from "../assets/img/proj/prj.png";
import project404Image from "../assets/img/404-not-found-cute.png";
import 'animate.css';
import TrackVisibility from 'react-on-screen';

export const Projects = () => {
  const projects = [
    {
      title: "Three.js React",
      description: "Experimental interactive portfolio build",
      imgUrl: projectLabImage,
      href: "https://lab.minhhieuportfolio.space/",
      stack: ["Three.js", "React"],
      accent: "accent-pink",
      summary: "A live lab build focused on interaction, visual direction, and frontend experiments inside the portfolio.",
    },
    {
      title: "Finance Dashboard",
      description: "Data-heavy admin workspace",
      imgUrl: project404Image,
      isPlaceholder: true,
      href: "https://lab.minhhieuportfolio.space/",
      stack: ["Next.js", "Charting", "Design System"],
      accent: "accent-blue",
      summary: "Dense operational UI made readable with hierarchy, contrast, and progressive disclosure.",
    },
    {
      title: "Lifestyle Commerce",
      description: "Visual storefront with editorial rhythm",
      imgUrl: project404Image,
      isPlaceholder: true,
      href: "https://lab.minhhieuportfolio.space/",
      stack: ["React", "CMS", "Responsive UI"],
      accent: "accent-gold",
      summary: "Product storytelling blended with friction-light browsing across mobile and desktop.",
    },
    {
      title: "Music Event Platform",
      description: "Immersive campaign microsite",
      imgUrl: project404Image,
      isPlaceholder: true,
      href: "https://lab.minhhieuportfolio.space/",
      stack: ["Animation", "Performance", "Front-end"],
      accent: "accent-blue",
      summary: "A punchy one-page build with atmosphere, motion, and strong CTA framing.",
    },
    {
      title: "Productivity SaaS",
      description: "Workflow dashboard and onboarding",
      imgUrl: project404Image,
      isPlaceholder: true,
      href: "https://lab.minhhieuportfolio.space/",
      stack: ["UX Strategy", "React", "Systems"],
      accent: "accent-pink",
      summary: "Reduced setup friction by turning a complex workflow into a guided product narrative.",
    },
    {
      title: "Portfolio Reframe",
      description: "Personal brand refresh",
      imgUrl: project404Image,
      isPlaceholder: true,
      href: "https://lab.minhhieuportfolio.space/",
      stack: ["Identity", "UI Polish", "Front-end"],
      accent: "accent-gold",
      summary: "Sharper typography, cinematic sections, and cleaner storytelling for stronger first impression.",
    },
  ];

  const systems = [
    {
      label: "Frontend Systems",
      title: "Interfaces that feel deliberate",
      text: "I build UI with strong type hierarchy, responsive structure, and motion that supports the content instead of distracting from it.",
      tags: ["React", "Next.js", "Bootstrap", "Responsive"],
    },
    {
      label: "Product Thinking",
      title: "Clarity before decoration",
      text: "I start from the user journey, then shape layout, interaction, and pacing so the page sells the idea in seconds.",
      tags: ["UX", "Interaction", "Wireframe", "Content Flow"],
    },
    {
      label: "Delivery",
      title: "Fast builds without messy code",
      text: "The goal is visual impact and maintainability together: reusable sections, clean CSS structure, and components that stay editable.",
      tags: ["Maintainable", "Scalable", "Shipping"],
    },
  ];

  const highlights = [
    { value: "04+", label: "Years refining frontend craft" },
    { value: "12", label: "Design directions explored before shipping" },
    { value: "100%", label: "Focus on mobile-first clarity" },
  ];

  const process = [
    {
      step: "01",
      title: "Read the room",
      text: "Understand the product angle, target audience, and what should hit in the first screen.",
    },
    {
      step: "02",
      title: "Shape the rhythm",
      text: "Turn sections into a sequence with contrast, pauses, punchy headlines, and clear anchors.",
    },
    {
      step: "03",
      title: "Polish the details",
      text: "Refine spacing, glow, overlays, and states until the UI feels intentional instead of template-made.",
    },
  ];

  return (
    <section className="project" id="projects">
      <Container>
        <Row>
          <Col size={12}>
            <TrackVisibility>
              {({ isVisible }) =>
              <div className={isVisible ? "animate__animated animate__fadeIn": ""}>
                <span className="project-kicker">Selected Work</span>
                <h2>Tabs that used to be filler now sell your range.</h2>
                <p>
                  This section is now structured like a proper showcase: featured builds, system thinking,
                  and a sharp process story. The goal is instant credibility, not placeholder content.
                </p>
                <Tab.Container id="projects-tabs" defaultActiveKey="first">
                  <Nav variant="pills" className="nav-pills mb-5 justify-content-center align-items-center" id="pills-tab">
                    <Nav.Item>
                      <Nav.Link eventKey="first">Featured Drops</Nav.Link>
                    </Nav.Item>
                    <Nav.Item>
                      <Nav.Link eventKey="second">Build DNA</Nav.Link>
                    </Nav.Item>
                    <Nav.Item>
                      <Nav.Link eventKey="third">Work Method</Nav.Link>
                    </Nav.Item>
                  </Nav>
                  <Tab.Content id="slideInUp" className={isVisible ? "animate__animated animate__slideInUp" : ""}>
                    <Tab.Pane eventKey="first">
                      <div className="projects-hero-panel">
                        <div>
                          <span className="eyebrow">Creative engineering</span>
                          <h3>Visual punch with structure underneath.</h3>
                        </div>
                        <p>
                          Each card highlights not just the outcome, but the type of problem solved
                          and the stack used to get there.
                        </p>
                      </div>
                      <Row>
                        {
                          projects.filter((project) => !project.isPlaceholder).map((project, index) => {
                            return (
                              <ProjectCard
                                key={index}
                                {...project}
                                />
                            )
                          })
                        }
                      </Row>
                      </Tab.Pane>
                      <Tab.Pane eventKey="second">
                        <div className="project-detail-shell">
                          <Row className="g-4 align-items-stretch">
                            <Col lg={7}>
                              <div className="insight-panel">
                                <span className="eyebrow">Core strengths</span>
                                <h3>Strong visuals only work when the structure is tight.</h3>
                                <p>
                                  The best portfolio sections do two jobs at once: they look expensive
                                  and they communicate fast. This tab makes that capability explicit.
                                </p>
                                <div className="systems-grid">
                                  {systems.map((item) => (
                                    <article className="system-card" key={item.title}>
                                      <span>{item.label}</span>
                                      <h4>{item.title}</h4>
                                      <p>{item.text}</p>
                                      <div className="system-tags">
                                        {item.tags.map((tag) => (
                                          <strong key={tag}>{tag}</strong>
                                        ))}
                                      </div>
                                    </article>
                                  ))}
                                </div>
                              </div>
                            </Col>
                            <Col lg={5}>
                              <div className="stats-panel">
                                <span className="eyebrow">Impact cues</span>
                                <h3>What this portfolio now signals</h3>
                                <div className="highlight-list">
                                  {highlights.map((item) => (
                                    <div className="highlight-item" key={item.label}>
                                      <strong>{item.value}</strong>
                                      <span>{item.label}</span>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            </Col>
                          </Row>
                        </div>
                      </Tab.Pane>
                      <Tab.Pane eventKey="third">
                        <div className="project-detail-shell">
                          <div className="process-panel">
                            <div className="process-intro">
                              <span className="eyebrow">From rough idea to polished section</span>
                              <h3>A process that keeps things sharp, not generic.</h3>
                              <p>
                                This closes the loop: you are not just showing finished screens,
                                you are showing how you think when shaping a product-facing experience.
                              </p>
                            </div>
                            <div className="process-timeline">
                              {process.map((item) => (
                                <article className="process-step" key={item.step}>
                                  <span>{item.step}</span>
                                  <h4>{item.title}</h4>
                                  <p>{item.text}</p>
                                </article>
                              ))}
                            </div>
                          </div>
                        </div>
                    </Tab.Pane>
                  </Tab.Content>
                </Tab.Container>
              </div>}
            </TrackVisibility>
          </Col>
        </Row>
      </Container>
    </section>
  )
}
